import { useState, useEffect } from 'react';
import { X, Users, Calendar, MapPin, Clock, Phone, Mail, AlertCircle, FileText, Camera, Sparkles, Shield, CheckCircle, UserCheck, CreditCard, Banknote, Info } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { registrationService } from '../../../services/registrationService';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';
import { validateFullName, validateEmail, validatePhone } from '../../../utils/turkishValidation';
import { formatTurkishPhone } from '../../../utils/phoneFormatter';

export default function BookingFormModal({ isOpen, onClose, eventId, pricing, capacity, currentBookings, onConfirmBooking, selectedTicketType, eventOptions }) {
  const [formData, setFormData] = useState({
    participantName: '',
    participantEmail: '',
    participantPhone: '',
    equipmentRental: false,
    specialRequirements: '',
    wantsLifeVest: false,
    wantsSupMotor: false,
    wantsWaterproofBag: false,
    wantsCancellationInsurance: false,
    acceptsLiabilityWaiver: false,
    acceptsImageRights: false,
    selectedCustomOptions: [],
    paymentMethod: 'eft'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [showLiabilityModal, setShowLiabilityModal] = useState(false);
  const [showImageRightsModal, setShowImageRightsModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      loadUserData();
      loadEventData();
    }
  }, [isOpen]);

  useEffect(() => {
    // Auto-select equipment rental if user chose rental board option
    if (selectedTicketType === 'rental') {
      setFormData(prev => ({
        ...prev,
        equipmentRental: true
      }));
    }
  }, [selectedTicketType, isOpen]);

  const loadEventData = async () => {
    try {
      const { data: event, error: eventError } = await supabase
        ?.from('events')
        ?.select('*')
        ?.eq('id', eventId)
        ?.single();

      if (eventError) throw eventError;
      setEventData(event);
    } catch (err) {
      console.error('Error loading event data:', err);
    }
  };

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        setError('Rezervasyon yapmak için giriş yapmalısınız');
        return;
      }
      setCurrentUser(user);

      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.single();

      if (profileError) throw profileError;

      setUserProfile(profile);

      const requiredFields = [
        { key: 'full_name', label: 'Ad Soyad' },
        { key: 'phone', label: 'Telefon Numarası' },
        { key: 'experience_level', label: 'Deneyim Seviyesi' },
        { key: 'tc_number', label: 'TC Kimlik Numarası' },
        { key: 'birth_date', label: 'Doğum Tarihi' },
        { key: 'emergency_contact_name', label: 'Acil Durum Kişisi Adı' },
        { key: 'emergency_contact_phone', label: 'Acil Durum Kişisi Telefonu' },
        { key: 'location', label: 'Konum' },
        { key: 'instagram_handle', label: 'Sosyal Medya Adresi' },
      ];

      const missing = requiredFields?.filter(field => {
        const val = profile?.[field?.key];
        if (field?.key === 'tc_number') {
          return !val || String(val)?.trim()?.length !== 11;
        }
        return !val || String(val)?.trim() === '';
      });

      setMissingFields(missing);

      setFormData({
        participantName: profile?.full_name || '',
        participantEmail: profile?.email || user?.email || '',
        participantPhone: profile?.phone || '',
        equipmentRental: selectedTicketType === 'rental',
        specialRequirements: '',
        wantsLifeVest: false,
        wantsSupMotor: false,
        wantsWaterproofBag: false,
        wantsCancellationInsurance: false,
        acceptsLiabilityWaiver: false,
        acceptsImageRights: false
      });
    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Kullanıcı bilgileri yüklenemedi');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!currentUser) {
        throw new Error('Rezervasyon yapmak için giriş yapmalısınız');
      }

      // Validate form data with Turkish character support
      const nameError = validateFullName(formData?.participantName);
      if (nameError) throw new Error(nameError);

      const emailError = validateEmail(formData?.participantEmail);
      if (emailError) throw new Error(emailError);

      const phoneError = validatePhone(formData?.participantPhone);
      if (phoneError) throw new Error(phoneError);

      // Validate consent checkboxes
      if (!formData?.acceptsLiabilityWaiver) {
        throw new Error('Sorumluluk feragatnamesini kabul etmelisiniz');
      }

      if (!formData?.acceptsImageRights) {
        throw new Error('Görüntü kullanım izinlerini kabul etmelisiniz');
      }

      // Create registration in Supabase
      const registrationData = await registrationService?.create({
        eventId: eventId,
        participantName: formData?.participantName,
        participantEmail: formData?.participantEmail,
        participantPhone: formData?.participantPhone,
        equipmentRental: formData?.equipmentRental,
        specialRequirements: formData?.specialRequirements,
        wantsLifeVest: formData?.wantsLifeVest,
        wantsSupMotor: formData?.wantsSupMotor,
        wantsWaterproofBag: formData?.wantsWaterproofBag,
        wantsCancellationInsurance: formData?.wantsCancellationInsurance,
        paymentMethod: formData?.paymentMethod === 'credit_card' ? 'Kredi Kartı' : 'EFT/Havale',
        selectedCustomOptions: (formData?.selectedCustomOptions || [])?.map(idx => {
          const opt = eventData?.custom_options?.[idx];
          return opt ? { title: opt?.title, price: parseFloat(opt?.price) || 0 } : null;
        })?.filter(Boolean),
        totalPrice: calculateGrandTotal(),
        ticketType: selectedTicketType === 'rental' ? 'rental_board' : selectedTicketType === 'standard' ? 'standard_ticket' : 'own_board',
        boardRented: selectedTicketType === 'rental',
        eventTitle: eventData?.title || '',
      });

      // Send pending confirmation email (non-blocking)
      try {
        await supabase?.functions?.invoke('send-ticket-email', {
          body: {
            participantEmail: formData?.participantEmail,
            participantName: formData?.participantName,
            eventTitle: eventData?.title || '',
            eventDate: eventData?.event_date || eventData?.date || '',
            eventTime: eventData?.event_time || eventData?.time || '',
            eventLocation: eventData?.location || '',
            reservationId: registrationData?.id,
            emailType: 'pending',
          },
        });
      } catch (emailErr) {
        console.error('Pending email failed (non-blocking):', emailErr);
      }

      // Call parent callback with registration data
      if (onConfirmBooking) {
        onConfirmBooking({
          ...registrationData,
          eventId: eventId,
          status: 'pending'
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setFormData({
          participantName: '',
          participantEmail: '',
          participantPhone: '',
          equipmentRental: false,
          specialRequirements: '',
          wantsLifeVest: false,
          wantsSupMotor: false,
          wantsWaterproofBag: false,
          wantsCancellationInsurance: false,
          acceptsLiabilityWaiver: false,
          acceptsImageRights: false
        });
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err?.message || 'Rezervasyon oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    if (name === 'participantPhone') {
      setFormData(prev => ({
        ...prev,
        participantPhone: formatTurkishPhone(value)
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Calculate total price dynamically
  const calculateTotalPrice = () => {
    // Base price
    let basePrice = formData?.equipmentRental 
      ? (pricing?.rental || pricing?.priceWithRental || 0) 
      : (pricing?.ownBoard || pricing?.priceOwnBoard || pricing?.basePrice || 0);
    
    // Add equipment extras
    let equipmentTotal = basePrice;
    
    if (formData?.wantsLifeVest && eventData?.price_life_vest > 0) {
      equipmentTotal += eventData?.price_life_vest;
    }
    
    if (formData?.wantsSupMotor && eventData?.price_sup_motor > 0) {
      equipmentTotal += eventData?.price_sup_motor;
    }
    
    if (formData?.wantsWaterproofBag && eventData?.price_waterproof_bag > 0) {
      equipmentTotal += eventData?.price_waterproof_bag;
    }

    // Add selected custom options
    const customOptions = eventData?.custom_options || [];
    const selectedCustomOptions = formData?.selectedCustomOptions || [];
    selectedCustomOptions?.forEach(optIndex => {
      const opt = customOptions?.[optIndex];
      if (opt) {
        equipmentTotal += parseFloat(opt?.price) || 0;
      }
    });
    
    // Add cancellation insurance (10% of current subtotal)
    let finalTotal = equipmentTotal;
    if (formData?.wantsCancellationInsurance) {
      finalTotal += (equipmentTotal * 0.10);
    }

    return finalTotal;
  };

  const calculateCreditCardFee = () => {
    if (formData?.paymentMethod !== 'credit_card') return 0;
    return calculateTotalPrice() * 0.03;
  };

  const calculateGrandTotal = () => {
    return calculateTotalPrice() + calculateCreditCardFee();
  };

  const handleCustomOptionToggle = (index) => {
    const customOptionsType = eventData?.custom_options_type || 'multiple';
    if (customOptionsType === 'single') {
      // Radio behavior: replace selection with the new one (deselect if same)
      const current = formData?.selectedCustomOptions || [];
      const updated = current?.includes(index) ? [] : [index];
      setFormData(prev => ({ ...prev, selectedCustomOptions: updated }));
    } else {
      // Checkbox behavior: toggle
      const current = formData?.selectedCustomOptions || [];
      const updated = current?.includes(index)
        ? current?.filter(i => i !== index)
        : [...current, index];
      setFormData(prev => ({ ...prev, selectedCustomOptions: updated }));
    }
  };

  if (!isOpen) return null;

  // Show missing profile fields warning instead of booking form
  if (missingFields?.length > 0) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-5 rounded-t-2xl">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-all duration-200 group"
            >
              <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-200" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Profil Bilgileri Eksik</h2>
                <p className="text-orange-100 text-xs mt-0.5">Etkinliğe kayıt olmak için profil bilgilerinizi tamamlayın</p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-5">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 font-medium">
                  Etkinliğe kayıt olabilmek için aşağıdaki profil bilgilerini doldurmanız gerekmektedir:
                </p>
              </div>
              <ul className="space-y-1.5">
                {missingFields?.map((field) => (
                  <li key={field?.key} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0"></div>
                    <span className="text-sm text-amber-900 font-medium">{field?.label}</span>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-gray-500 mb-4 text-center">
              Bu bilgiler güvenliğiniz ve etkinlik organizasyonu için zorunludur.
            </p>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors duration-200"
              >
                Vazgeç
              </button>
              <button
                onClick={() => {
                  onClose();
                  navigate('/user-profile');
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl text-sm font-semibold hover:from-blue-700 hover:to-cyan-600 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Profili Tamamla
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl">
          <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-3">Rezervasyon Başarılı!</h3>
          <p className="text-gray-600 text-lg">
            Rezervasyonunuz alındı. Organizatör onayladıktan sonra e-posta ile bilgilendirileceksiniz.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 p-4 rounded-t-2xl flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 hover:bg-white/20 rounded-full transition-all duration-200 group"
          >
            <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-200" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Rezervasyon Formu</h2>
          </div>
          <p className="text-blue-50 text-sm ml-8">{eventData?.title || 'Etkinlik'}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable Form Fields */}
          <div className="overflow-y-auto flex-1 p-4 space-y-4 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-blue-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-blue-400">

          {/* Event Info Summary - scrolls naturally with form content */}
          {eventData && (
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-100">
              <div className="grid grid-cols-2 gap-2 p-2">
                <div className="flex items-center gap-2 bg-white/60 rounded-lg p-2 backdrop-blur-sm">
                  <div className="p-1.5 bg-blue-100 rounded-md flex-shrink-0">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700 leading-tight">
                    {new Date(eventData?.event_date)?.toLocaleDateString('tr-TR', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 rounded-lg p-2 backdrop-blur-sm">
                  <div className="p-1.5 bg-blue-100 rounded-md flex-shrink-0">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">{eventData?.event_time}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 rounded-lg p-2 backdrop-blur-sm">
                  <div className="p-1.5 bg-blue-100 rounded-md flex-shrink-0">
                    <MapPin className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700 leading-tight">{eventData?.location}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/60 rounded-lg p-2 backdrop-blur-sm">
                  <div className="p-1.5 bg-blue-100 rounded-md flex-shrink-0">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-gray-700">
                    Kapasite: {eventData?.max_capacity || eventData?.capacity || capacity} Kişi
                  </span>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3 flex items-start gap-2 shadow-sm">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-800 font-medium">{error}</p>
            </div>
          )}

          {/* Personal Information Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="h-1 w-1 bg-blue-600 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900">Kişisel Bilgiler</h3>
            </div>

            {/* Participant Name */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Ad Soyad <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="participantName"
                value={formData?.participantName}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                placeholder="Adınız ve soyadınız"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                E-posta <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  name="participantEmail"
                  value={formData?.participantEmail}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                  placeholder="ornek@email.com"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Telefon <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  name="participantPhone"
                  value={formData?.participantPhone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400"
                  placeholder="0532 123 45 67"
                  maxLength={14}
                />
              </div>
            </div>
          </div>

          {/* Equipment Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="h-1 w-1 bg-blue-600 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900">Ekipman Seçenekleri</h3>
            </div>

            {/* Equipment Rental */}
            {pricing && (pricing?.rental || pricing?.priceWithRental) > (pricing?.ownBoard || pricing?.priceOwnBoard || 0) && (
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-3 border-2 border-blue-100 shadow-sm hover:shadow-md transition-shadow duration-200">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    name="equipmentRental"
                    checked={formData?.equipmentRental}
                    onChange={handleChange}
                    className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900 text-sm">Paddleboard Kiralama</span>
                      <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                        +{Math.round((pricing?.rental || pricing?.priceWithRental || 0) - (pricing?.ownBoard || pricing?.priceOwnBoard || 0))} ₺
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-2">
                      Kendi ekipmanınız yoksa kiralayabilirsiniz
                    </p>
                    <div className="bg-white/80 backdrop-blur-sm rounded-lg p-2 border border-blue-200">
                      <p className="text-xs font-semibold text-blue-900 mb-0.5">📦 Dahil olan ekipmanlar:</p>
                      <p className="text-xs text-blue-700">Paddleboard, Kürek, Leash ve Can Yeleği</p>
                    </div>
                  </div>
                </label>
              </div>
            )}

            {/* Extra Equipment Options */}
            <div className="grid grid-cols-2 gap-2">
              {/* Waterproof Bag - position 1 (short, no subtext) */}
              {eventData?.price_waterproof_bag > 0 && (
                <div className="bg-gray-50 rounded-lg p-2.5 border-2 border-gray-200 hover:border-blue-300 transition-colors duration-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="wantsWaterproofBag"
                      checked={formData?.wantsWaterproofBag}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-gray-900 block text-xs">Su Geçirmez Çanta</span>
                      <span className="text-xs text-blue-600 font-medium whitespace-nowrap">+{Math.round(eventData?.price_waterproof_bag)} ₺</span>
                    </div>
                  </label>
                </div>
              )}

              {/* SUP Motor - position 2 (short, no subtext) */}
              {eventData?.price_sup_motor > 0 && (
                <div className="bg-gray-50 rounded-lg p-2.5 border-2 border-gray-200 hover:border-blue-300 transition-colors duration-200">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="wantsSupMotor"
                      checked={formData?.wantsSupMotor}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-gray-900 block text-xs">SUP Motoru</span>
                      <span className="text-xs text-blue-600 font-medium whitespace-nowrap">+{Math.round(eventData?.price_sup_motor)} ₺</span>
                    </div>
                  </label>
                </div>
              )}

              {/* Life Vest - position 3 (tall, has subtext) */}
              {eventData?.price_life_vest > 0 && (
                <div className="bg-gray-50 rounded-lg p-2.5 border-2 border-gray-200 hover:border-blue-300 transition-colors duration-200">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="wantsLifeVest"
                      checked={formData?.wantsLifeVest}
                      onChange={handleChange}
                      className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                    />
                    <div className="flex-1">
                      <span className="font-semibold text-gray-900 block text-xs">Can Yeleği</span>
                      <span className="text-xs text-blue-600 font-medium whitespace-nowrap">+{Math.round(eventData?.price_life_vest)} ₺</span>
                      <p className="text-xs text-gray-500 mt-0.5 leading-tight">Paddleboard kiralayan katılımcılarımız için ekipmana dahildir.</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Cancellation Insurance - position 4 (tall, has subtext) */}
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-2.5 border-2 border-amber-200 hover:border-amber-300 transition-colors duration-200">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="wantsCancellationInsurance"
                    checked={formData?.wantsCancellationInsurance}
                    onChange={handleChange}
                    className="mt-0.5 w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Shield className="w-3 h-3 text-amber-600" />
                      <span className="font-semibold text-gray-900 text-xs">İptal Garantisi</span>
                      <span className="text-xs text-amber-700 font-medium">(%10)</span>
                    </div>
                    <p className="text-xs text-gray-600">
                      24 saat kalana kadar iptal edebilir ve kesintisiz iade alırsınız
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Custom Extra Options */}
          {eventData?.custom_options?.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="h-1 w-1 bg-blue-600 rounded-full"></div>
                <h3 className="text-sm font-bold text-gray-900">Ekstra Seçenekler</h3>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {eventData?.custom_options?.map((option, index) => {
                  const isSelected = formData?.selectedCustomOptions?.includes(index);
                  const isSingle = (eventData?.custom_options_type || 'multiple') === 'single';
                  return (
                    <div key={index} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg p-2.5 border-2 border-indigo-100 hover:border-indigo-300 transition-colors duration-200">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type={isSingle ? 'radio' : 'checkbox'}
                          name={isSingle ? 'customOptionSingle' : undefined}
                          checked={isSelected}
                          onChange={() => handleCustomOptionToggle(index)}
                          className={`w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 cursor-pointer ${isSingle ? 'rounded-full' : 'rounded'}`}
                        />
                        <div className="flex-1 flex items-center justify-between">
                          <span className="font-semibold text-gray-900 text-xs">{option?.title}</span>
                          <span className="text-xs text-indigo-600 font-bold whitespace-nowrap">+{Math.round(parseFloat(option?.price || 0))} ₺</span>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Payment Method */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="h-1 w-1 bg-blue-600 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900">Ödeme Yöntemi</h3>
            </div>
            <div className="space-y-2">
              {/* EFT/Havale */}
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-blue-300 cursor-pointer transition-colors duration-200">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="eft"
                  checked={formData?.paymentMethod === 'eft'}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <Banknote className="w-4 h-4 text-gray-600" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900 text-sm">EFT / Havale</span>
                  <p className="text-xs text-gray-500">Banka havalesi ile ödeme</p>
                </div>
              </label>

              {/* Credit Card */}
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-blue-300 cursor-pointer transition-colors duration-200">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="credit_card"
                  checked={formData?.paymentMethod === 'credit_card'}
                  onChange={handleChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <CreditCard className="w-4 h-4 text-gray-600" />
                <div className="flex-1">
                  <span className="font-semibold text-gray-900 text-sm">Kredi Kartı ile Öde</span>
                  <p className="text-xs text-gray-500">%3 banka işlem ücreti uygulanır</p>
                </div>
              </label>
            </div>

            {/* Credit Card Warning Box */}
            {formData?.paymentMethod === 'credit_card' && (
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800">
                  Kredi kartı ile yapılan ödemelerde %3 banka işlem ücreti yansıtılmaktadır. Anlayışınız için teşekkür ederiz.
                </p>
              </div>
            )}
          </div>

          {/* Special Requirements */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <div className="h-1 w-1 bg-blue-600 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900">Özel İstekler</h3>
            </div>
            <textarea
              name="specialRequirements"
              value={formData?.specialRequirements}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm text-gray-900 placeholder-gray-400 resize-none"
              placeholder="Herhangi bir özel isteğiniz veya bildirmek istediğiniz bir durum var mı?"
            />
          </div>

          {/* Consent Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="h-1 w-1 bg-blue-600 rounded-full"></div>
              <h3 className="text-sm font-bold text-gray-900">Onaylar</h3>
            </div>

            {/* Liability Waiver Checkbox */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-2.5 border-2 border-blue-200 hover:border-blue-300 transition-all duration-200 shadow-sm">
              <label className="flex items-start gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  name="acceptsLiabilityWaiver"
                  checked={formData?.acceptsLiabilityWaiver}
                  onChange={handleChange}
                  className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-2 cursor-pointer"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <FileText className="w-3 h-3 text-blue-600" />
                    <span className="text-xs font-semibold text-gray-900">
                      Sorumluluk Feragatnamesini Okudum ve Kabul Ediyorum <span className="text-red-500">*</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowLiabilityModal(true)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all duration-200 hover:underline"
                  >
                    Feragatnameyi görüntüle →
                  </button>
                </div>
              </label>
            </div>

            {/* Image Rights Checkbox */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-2.5 border-2 border-purple-200 hover:border-purple-300 transition-all duration-200 shadow-sm">
              <label className="flex items-start gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  name="acceptsImageRights"
                  checked={formData?.acceptsImageRights}
                  onChange={handleChange}
                  className="mt-0.5 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-offset-2 cursor-pointer"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Camera className="w-3 h-3 text-purple-600" />
                    <span className="text-xs font-semibold text-gray-900">
                      Görüntü Kullanım İzinlerini Kabul Ediyorum <span className="text-red-500">*</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowImageRightsModal(true)}
                    className="text-xs text-purple-600 hover:text-purple-700 font-medium inline-flex items-center gap-1 hover:gap-1.5 transition-all duration-200 hover:underline"
                  >
                    İzin metnini görüntüle →
                  </button>
                </div>
              </label>
            </div>
          </div>
          </div>

          {/* Sticky Footer: Price Summary + Submit Button */}
          <div className="flex-shrink-0 border-t border-gray-200 p-2 space-y-2 bg-white rounded-b-2xl">
            {/* Price Summary */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-2 border-2 border-green-200 shadow-sm">
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-gray-700">Ara Toplam</span>
                  <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                    {Math.round(calculateTotalPrice())} ₺
                  </span>
                </div>
                {formData?.paymentMethod === 'credit_card' && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-orange-700 whitespace-nowrap">Kredi Kartı İşlem Ücreti (+%3)</span>
                    <span className="text-xs font-semibold text-orange-700 whitespace-nowrap">
                      +{Math.round(calculateCreditCardFee())} ₺
                    </span>
                  </div>
                )}
                <div className="border-t border-green-200 pt-1 flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-900 whitespace-nowrap">Toplam Tutar</span>
                  <span className="text-lg font-bold text-green-600 whitespace-nowrap">
                    {Math.round(calculateGrandTotal())} ₺
                  </span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-1.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-semibold hover:border-gray-400 text-sm"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading || !formData?.acceptsLiabilityWaiver || !formData?.acceptsImageRights}
                className="flex-1 px-4 py-1.5 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:shadow-none text-sm"
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span>Gönderiliyor...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Rezervasyon Yap</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Liability Waiver Modal */}
        {showLiabilityModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-bold text-gray-900">Sorumluluk Feragatnamesi</h3>
                </div>
                <button
                  onClick={() => setShowLiabilityModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <div className="prose prose-sm max-w-none">
                  <h4 className="font-bold text-gray-900 mb-3">1. Genel Hükümler</h4>
                  <p className="text-gray-700 mb-4">
                    Bu feragatname, etkinliğe katılma sırasında oluşabilecek her türlü risk ve sorumluluklar hakkında katılımcıyı bilgilendirmek amacıyla hazırlanmıştır. Etkinliğe katılarak, aşağıdaki şartları okuduğunuzu, anladığınızı ve kabul ettiğinizi beyan edersiniz.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">2. Risklerin Kabulü</h4>
                  <p className="text-gray-700 mb-4">
                    Outdoor aktiviteler doğası gereği risk içermektedir. Katılımcı, etkinlik sırasında karşılaşabileceği fiziksel yaralanma, ekipman hasarı, hava koşulları, doğal tehlikeler ve diğer öngörülemeyen durumların farkındadır ve bu riskleri kabul eder.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">3. Sağlık Beyanı</h4>
                  <p className="text-gray-700 mb-4">
                    Katılımcı, etkinliğe katılmaya engel teşkil edecek herhangi bir sağlık sorunu olmadığını, gerekli fiziksel kondisyona sahip olduğunu ve gerekli ilaçlarını yanında bulundurduğunu beyan eder.
                  </p>
                  <p className="text-gray-700 mb-4 font-semibold">
                    <strong>Önemli:</strong> Katılımcı, yüzme bildiğini ve bu yeteneğin etkinliğe katılma için zorunlu olduğunu kabul eder. Yüzme bilmeyen katılımcılar güvenlik nedeniyle etkinliğe katılamaz.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">4. Sorumluluk Feragati</h4>
                  <p className="text-gray-700 mb-4">
                    Katılımcı, etkinlik organizatörünü, rehberleri ve ilgili tüm tarafları, etkinlik sırasında veya sonrasında meydana gelebilecek her türlü yaralanma, hasar, kayıp veya ölüm durumlarından sorumlu tutmayacağını kabul eder.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">5. Güvenlik Kurallarına Uyum</h4>
                  <p className="text-gray-700 mb-4">
                    Katılımcı, etkinlik öncesi ve sırasında verilen tüm güvenlik talimatlarına uyacağını, gerekli güvenlik ekipmanlarını kullanacağını ve organizatör tarafından belirlenen kurallara riayet edeceğini taahhüt eder.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">6. Sigorta</h4>
                  <p className="text-gray-700 mb-4">
                    Katılımcının kendi sağlık ve kaza sigortasına sahip olması önerilir. Organizatör, katılımcıların sigorta durumlarından sorumlu değildir.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">7. İptal ve Değişiklik Hakları</h4>
                  <p className="text-gray-700 mb-4">
                    Organizatör, hava koşulları, güvenlik endişeleri veya öngörülemeyen durumlar nedeniyle etkinliği iptal etme veya değiştirme hakkını saklı tutar.
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowLiabilityModal(false)}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image Rights Modal */}
        {showImageRightsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Camera className="w-6 h-6 text-purple-600" />
                  <h3 className="text-xl font-bold text-gray-900">Görüntü Kullanım İzni</h3>
                </div>
                <button
                  onClick={() => setShowImageRightsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <div className="prose prose-sm max-w-none">
                  <h4 className="font-bold text-gray-900 mb-3">1. Görüntü Kullanım İzni</h4>
                  <p className="text-gray-700 mb-4">
                    Etkinlik sırasında çekilecek fotoğraf ve video görüntülerinin, organizatör tarafından tanıtım, pazarlama, sosyal medya paylaşımları ve diğer ticari olmayan amaçlarla kullanılmasına izin veriyorum.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">2. Kullanım Alanları</h4>
                  <p className="text-gray-700 mb-4">
                    Görüntüler aşağıdaki platformlarda kullanılabilir:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                    <li>Web sitesi ve blog yazıları</li>
                    <li>Sosyal medya hesapları (Instagram, Facebook, Twitter, vb.)</li>
                    <li>Tanıtım broşürleri ve kataloglar</li>
                    <li>E-posta bültenleri ve dijital pazarlama materyalleri</li>
                    <li>Basılı ve dijital reklamlar</li>
                  </ul>

                  <h4 className="font-bold text-gray-900 mb-3">3. Telif Hakları</h4>
                  <p className="text-gray-700 mb-4">
                    Etkinlik sırasında çekilen tüm görüntülerin telif hakları organizatöre aittir. Katılımcı, bu görüntüler üzerinde herhangi bir telif hakkı talebinde bulunmayacağını kabul eder.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">4. Ücret Talebi</h4>
                  <p className="text-gray-700 mb-4">
                    Katılımcı, görüntülerin kullanımı karşılığında herhangi bir ücret, telif veya tazminat talep etmeyeceğini kabul eder.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">5. Gizlilik ve Kişisel Veriler</h4>
                  <p className="text-gray-700 mb-4">
                    Görüntülerde yer alan kişisel bilgileriniz (ad, soyad, iletişim bilgileri) açık rıza olmadan paylaşılmayacaktır. Sadece görsel içerik kullanılacaktır.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">6. İtiraz Hakkı</h4>
                  <p className="text-gray-700 mb-4">
                    Katılımcı, paylaşılan herhangi bir görüntüye itiraz etme ve kaldırılmasını talep etme hakkına sahiptir. Bu durumda organizatör ile iletişime geçilmelidir.
                  </p>

                  <h4 className="font-bold text-gray-900 mb-3">7. İzin Süresi</h4>
                  <p className="text-gray-700 mb-4">
                    Bu izin, aksi belirtilmedikçe süresiz olarak geçerlidir. Katılımcı, izni geri çekme hakkını saklı tutar.
                  </p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200">
                <button
                  onClick={() => setShowImageRightsModal(false)}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}